package br.gov.go.pge.permanenciaweb.site.pages.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import br.gov.go.pge.permanenciaweb.shared.AccessGrant
import br.gov.go.pge.permanenciaweb.shared.AccessPreset
import br.gov.go.pge.permanenciaweb.shared.AdminUserDto
import br.gov.go.pge.permanenciaweb.shared.Capability
import br.gov.go.pge.permanenciaweb.shared.GrantDto
import br.gov.go.pge.permanenciaweb.shared.GrantScope
import br.gov.go.pge.permanenciaweb.shared.grants
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.components.ConfirmDialog
import br.gov.go.pge.permanenciaweb.site.components.DangerButtonVariant
import br.gov.go.pge.permanenciaweb.site.components.DefaultButtonVariant
import br.gov.go.pge.permanenciaweb.site.components.PrimaryButtonVariant
import br.gov.go.pge.permanenciaweb.site.components.ScreenReaderOnlyStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.Checkbox
import com.varabyte.kobweb.silk.components.forms.CheckboxSize
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

/*
 * Permission editor for one account. The draft is local until "Salvar": the server receives the
 * whole editable set, computes the diff and applies it in one transaction. Sync-managed grants are
 * shown locked, because the next `dim_chefia` push would restore them anyway.
 *
 * Visual grammar: one line per permission (label + short description on the left, control on the
 * right), sections split by horizontal rules. Long explanations move to `title` tooltips.
 */

internal enum class AccessTab { PERMISSIONS, HISTORY }

internal class AccessEditorState(
    val user: AdminUserDto,
    /** True when this is the only active `MANAGE_ACCESS` holder; removing it is blocked. */
    val isLastAdmin: Boolean = false,
    /** True when the editor was opened for the signed-in account. */
    val isSelf: Boolean = false,
) {
    val sync: Set<AccessGrant> = user.grants.syncGrantSet()
    private val original: Set<AccessGrant> = user.grants.toGrantSet() - sync

    var draft by mutableStateOf(original)
    var tab by mutableStateOf(AccessTab.PERMISSIONS)
    var history by mutableStateOf<List<GrantDto>>(emptyList())
    var historyLoaded by mutableStateOf(false)
    var historyError by mutableStateOf("")
    var saving by mutableStateOf(false)
    var error by mutableStateOf("")

    val effective: Set<AccessGrant> get() = sync + draft
    val added: Set<AccessGrant> get() = draft - original
    val removed: Set<AccessGrant> get() = original - draft
    val dirty: Boolean get() = added.isNotEmpty() || removed.isNotEmpty()

    /** The API also refuses to deny/deactivate the last active administrator. */
    val denyBlockedByLastAdmin: Boolean get() = isLastAdmin

    /** True when saving would remove `MANAGE_ACCESS` from an active account. */
    val isDemotingAdmin: Boolean
        get() = user.status == "ACTIVE" &&
            user.grants.has(Capability.MANAGE_ACCESS) &&
            !effective.grants(Capability.MANAGE_ACCESS)

    /** Removing the signed-in account's own admin grant needs a stronger confirmation. */
    val isSelfDemotion: Boolean get() = isSelf && isDemotingAdmin

    /**
     * Presets are all-or-nothing. The last admin cannot apply the ones that drop `MANAGE_ACCESS`;
     * the defensive guard in [applyPreset] keeps the draft consistent even if a caller forgets it.
     */
    fun presetWouldRemoveLastAdmin(preset: AccessPreset): Boolean =
        isLastAdmin && !preset.grants.grants(Capability.MANAGE_ACCESS)

    fun locked(capability: Capability): Boolean = sync.any { it.capability == capability }

    fun syncScope(capability: Capability): GrantScope? =
        sync.firstOrNull { it.capability == capability }?.scope

    fun flag(capability: Capability): Boolean =
        draft.any { it.capability == capability && it.scope == GrantScope.GLOBAL }

    fun toggle(capability: Capability) {
        if (locked(capability) || capability.isScoped) return
        if (capability == Capability.MANAGE_ACCESS && isLastAdmin && flag(capability)) return
        draft = if (flag(capability)) {
            draft.filterNot { it.capability == capability }.toSet()
        } else {
            draft + AccessGrant(capability)
        }
    }

    fun scope(capability: Capability): GrantScope =
        draft.firstOrNull { it.capability == capability && it.scope != GrantScope.GLOBAL }?.scope
            ?: GrantScope.GLOBAL

    fun setScope(capability: Capability, scope: GrantScope) {
        if (!capability.isScoped) return
        val without = draft.filterNot { it.capability == capability }.toSet()
        draft = if (scope == GrantScope.GLOBAL) without else without + AccessGrant(capability, scope)
    }

    /**
     * Presets are shortcuts over the editable set; sync grants are never removed by one.
     * `VIEW_REPORT` is orthogonal and always an explicit decision, so a non-ADMIN preset
     * preserves the current report grant instead of silently removing it.
     */
    fun applyPreset(preset: AccessPreset) {
        if (user.isLeadership || presetWouldRemoveLastAdmin(preset)) return
        val report = draft.filter { it.capability == Capability.VIEW_REPORT }.toSet()
        draft = preset.grants + report
    }

    val selectedPreset: AccessPreset?
        get() {
            val report = draft.filter { it.capability == Capability.VIEW_REPORT }.toSet()
            return AccessPreset.entries.firstOrNull { it.grants + report == draft }
        }

    val canSubmit: Boolean get() = !saving && (user.status != "ACTIVE" || dirty)

    fun requiresReportHint(): Boolean =
        effective.grants(Capability.MANAGE_ROSTER_CORRECTIONS) &&
            !effective.grants(Capability.VIEW_REPORT)
}

val AccessEditorStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.5.cssRem)
        .padding(1.25.cssRem)
        .width(100.percent)
        .maxWidth(42.cssRem)
}

val AccessEditorHeaderStyle = CssStyle.base {
    Modifier.margin(0.cssRem)
}

val AccessEditorHintStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Muted).fontSize(0.75.cssRem).margin(0.cssRem)
}

/** One-line muted description under a capability label. */
val AccessCapabilityDescStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Muted).fontSize(0.6875.cssRem).margin(0.cssRem)
}

/** Stacks label + description inside the checkbox label (label stays clickable). */
val AccessFlagTextStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).gap(0.0625.cssRem)
}

/** Uppercase small header above a section; a rule above it separates the sections. */
val AccessSectionTitleStyle = CssStyle.base {
    Modifier
        .color(IndustrialPalette.Steel)
        .fontSize(0.6875.cssRem)
        .fontWeight(FontWeight.SemiBold)
        .margin(top = 0.625.cssRem, bottom = 0.375.cssRem)
}

/** Horizontal separator between editor sections. */
val AccessSectionRuleStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.Border)
        .height(1.px)
        .width(100.percent)
        .margin(top = 0.875.cssRem)
}

/** Scrollable area between the tabs and the footer. */
val AccessEditorBodyStyle = CssStyle.base {
    Modifier.width(100.percent).margin(top = 0.875.cssRem)
}

val AccessEditorFooterStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .justifyContent(com.varabyte.kobweb.compose.css.JustifyContent.FlexEnd)
        .gap(0.5.cssRem)
        .margin(top = 1.cssRem)
}

val AccessEditorErrorStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Red).fontSize(0.8125.cssRem).margin(top = 0.375.cssRem)
}

val AccessEditorDangerStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Red).fontSize(0.6875.cssRem).margin(0.cssRem)
}

val AccessDangerLabelStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Red)
}

/** History line: bold-ish capability on the left, muted provenance on the right. */
val AccessHistoryRowStyle = CssStyle.base {
    Modifier
        .borderBottom(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .padding(top = 0.3125.cssRem, bottom = 0.3125.cssRem)
        .width(100.percent)
}

val AccessHistoryLabelStyle = CssStyle.base {
    Modifier.fontSize(0.8125.cssRem).fontWeight(FontWeight.Medium)
}

val AccessHistoryRevokedStyle = CssStyle.base {
    Modifier.fontSize(0.8125.cssRem).color(IndustrialPalette.Muted)
}

/** Plain-language effective-permission summary at the bottom of the permissions tab. */
val AccessSummaryStyle = CssStyle.base {
    Modifier
        .color(IndustrialPalette.Muted)
        .fontSize(0.75.cssRem)
        .margin(top = 0.875.cssRem)
}

private enum class AccessConfirmation { REMOVE_ADMIN, DENY_ACCESS }

@Composable
internal fun AccessEditorModal(
    state: AccessEditorState,
    onClose: () -> Unit,
    onSave: () -> Unit,
    onDeny: (() -> Unit)? = null,
) {
    var confirmation by remember(state.user.username) { mutableStateOf<AccessConfirmation?>(null) }
    val confirmationOpen = confirmation != null

    // The confirmation owns Escape while it is open; disposing this listener prevents the key
    // press from closing the permissions modal underneath it.
    DisposableEffect(state.user.username, confirmationOpen) {
        if (confirmationOpen) {
            onDispose { }
        } else {
            val listener = EventListener { event ->
                if ((event as? KeyboardEvent)?.key == "Escape" && !state.saving) onClose()
            }
            window.addEventListener("keydown", listener)
            onDispose { window.removeEventListener("keydown", listener) }
        }
    }

    Overlay(contentAlignment = Alignment.Center) {
        Box(Modifier.fillMaxSize().onClick { if (!state.saving) onClose() })

        Column(
            modifier = AccessEditorStyle.toModifier()
                .then(Modifier.id("access-editor"))
                .then(
                    if (confirmationOpen) {
                        Modifier.attrsModifier { attr("inert", "") }
                    } else {
                        Modifier
                    }
                ),
        ) {
            H3(attrs = AccessEditorHeaderStyle.toAttrs()) {
                Text("Permissões de ${state.user.username}")
            }
            Row(
                modifier = Modifier.gap(0.375.cssRem).margin(top = 0.125.cssRem),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                StatusChip(state.user.status)
                // Só fala quando há algo além do status: chefia, pré-criação ou suspensão.
                val source = when {
                    state.user.isLeadership -> "Chefia sincronizada (dim_chefia)"
                    state.user.provisionedSource == "LEADERSHIP_SYNC" -> "Pré-criada por dim_chefia"
                    state.user.status == "DENIED" -> "Permissões suspensas até reativação."
                    else -> ""
                }
                if (source.isNotEmpty()) {
                    Span(attrs = AccessEditorHintStyle.toAttrs()) { Text(source) }
                }
            }

            if (state.isLastAdmin) {
                Span(attrs = ScreenReaderOnlyStyle.toAttrs { attr("id", "access-last-admin-hint") }) {
                    Text(LAST_ADMIN_REMOVAL_BLOCKED)
                }
            }

            Row(modifier = Modifier.gap(0.5.cssRem).margin(top = 0.875.cssRem)) {
                Button(
                    onClick = { state.tab = AccessTab.PERMISSIONS },
                    variant = if (state.tab == AccessTab.PERMISSIONS) PrimaryButtonVariant else DefaultButtonVariant,
                    size = ButtonSize.SM,
                ) { Text("Permissões") }
                Button(
                    onClick = { state.tab = AccessTab.HISTORY },
                    variant = if (state.tab == AccessTab.HISTORY) PrimaryButtonVariant else DefaultButtonVariant,
                    size = ButtonSize.SM,
                ) { Text("Histórico") }
            }

            Div(
                attrs = AccessEditorBodyStyle.toAttrs {
                    attr("style", "max-height:min(60vh,30rem);overflow-y:auto")
                },
            ) {
                when (state.tab) {
                    AccessTab.PERMISSIONS -> PermissionsTab(state)
                    AccessTab.HISTORY -> HistoryTab(state)
                }
            }

            if (state.error.isNotEmpty()) {
                P(attrs = AccessEditorErrorStyle.toAttrs { attr("role", "alert") }) { Text(state.error) }
            }

            Row(modifier = AccessEditorFooterStyle.toModifier()) {
                if (onDeny != null && state.user.status != "DENIED") {
                    val denyBlocked = state.denyBlockedByLastAdmin
                    Button(
                        onClick = { confirmation = AccessConfirmation.DENY_ACCESS },
                        variant = DangerButtonVariant,
                        size = ButtonSize.SM,
                        enabled = !state.saving && !denyBlocked,
                        modifier = if (denyBlocked) {
                            Modifier.attrsModifier {
                                attr("title", LAST_ADMIN_REMOVAL_BLOCKED)
                                attr("aria-describedby", "access-last-admin-hint")
                            }
                        } else {
                            Modifier
                        },
                    ) { Text("Negar acesso") }
                }
                Button(
                    onClick = { onClose() },
                    variant = DefaultButtonVariant,
                    size = ButtonSize.SM,
                    enabled = !state.saving,
                ) { Text("Cancelar") }
                Button(
                    onClick = {
                        if (state.isDemotingAdmin) {
                            confirmation = AccessConfirmation.REMOVE_ADMIN
                        } else {
                            onSave()
                        }
                    },
                    variant = PrimaryButtonVariant,
                    size = ButtonSize.SM,
                    enabled = state.canSubmit,
                ) {
                    Text(
                        when {
                            state.saving -> "Salvando…"
                            state.user.status == "PENDING" -> "Ativar conta"
                            state.user.status == "DENIED" -> "Reativar conta"
                            else -> "Salvar alterações"
                        }
                    )
                }
            }
        }
    }

    confirmation?.let { action ->
        val copy = when (action) {
            AccessConfirmation.REMOVE_ADMIN -> removeAdminConfirmation(
                username = state.user.username,
                isSelf = state.isSelf,
                keepsReport = state.effective.grants(Capability.VIEW_REPORT),
            )
            AccessConfirmation.DENY_ACCESS -> denyAccessConfirmation(
                username = state.user.username,
                isSelf = state.isSelf,
            )
        }
        ConfirmDialog(
            title = copy.title,
            message = copy.message,
            confirmLabel = copy.confirmLabel,
            selfWarning = copy.selfWarning,
            dialogId = "access-confirm",
            onCancel = { confirmation = null },
            onConfirm = {
                confirmation = null
                when (action) {
                    AccessConfirmation.REMOVE_ADMIN -> onSave()
                    AccessConfirmation.DENY_ACCESS -> onDeny?.invoke()
                }
            },
        )
    }
}

@Composable
private fun StatusChip(status: String) {
    val variant = when (statusChipTone(status)) {
        AccessChipTone.NORMAL -> null
        AccessChipTone.WARNING -> AccessChipWarningVariant
        AccessChipTone.DANGER -> AccessChipDangerVariant
        AccessChipTone.MUTED -> AccessChipMutedVariant
    }
    Span(attrs = AccessChipStyle.toModifier(variant).toAttrs()) { Text(statusLabel(status)) }
}

/** Small muted chip for provenance notes ("sincronizado", "último admin", …). */
@Composable
private fun AccessTag(text: String) {
    Span(attrs = AccessChipStyle.toModifier(AccessChipMutedVariant).toAttrs()) { Text(text) }
}

@Composable
private fun PermissionsTab(state: AccessEditorState) {
    if (state.user.isLeadership) {
        P(attrs = AccessEditorHintStyle.toAttrs()) {
            Text("O perfil de chefia é gerenciado pela sincronização. Ajuste apenas as permissões extras.")
        }
    } else {
        Column(modifier = Modifier.gap(0.375.cssRem)) {
            Row(modifier = Modifier.gap(0.375.cssRem)) {
                for (preset in AccessPreset.entries) {
                    val blocked = state.presetWouldRemoveLastAdmin(preset)
                    Button(
                        onClick = { state.applyPreset(preset) },
                        variant = if (state.selectedPreset == preset) PrimaryButtonVariant else DefaultButtonVariant,
                        size = ButtonSize.SM,
                        enabled = !blocked,
                        modifier = if (blocked) {
                            Modifier.attrsModifier {
                                attr("title", LAST_ADMIN_REMOVAL_BLOCKED)
                                attr("aria-describedby", "access-last-admin-hint")
                            }
                        } else {
                            Modifier
                        },
                    ) { Text(presetLabel(preset)) }
                }
            }
            P(attrs = AccessEditorHintStyle.toAttrs()) {
                Text(state.selectedPreset?.let { PRESET_HELP[it] } ?: "Ajuste as permissões abaixo.")
            }
        }
    }

    AccessSection("Painel") {
        PermissionFlag(
            state = state,
            capability = Capability.VIEW_REPORT,
        )
        PermissionFlag(
            state = state,
            capability = Capability.MANAGE_ROSTER_CORRECTIONS,
        )
        if (state.requiresReportHint()) {
            P(attrs = AccessEditorDangerStyle.toAttrs()) {
                Text("Sem 'Ver painel', 'Corrigir painel' não tem efeito.")
            }
        }
    }

    AccessSection("Liberações") {
        PermissionFlag(
            state = state,
            capability = Capability.CREATE_STAY_PERMIT,
        )
        PermissionScope(
            state = state,
            capability = Capability.VIEW_STAY_PERMITS,
        )
        PermissionScope(
            state = state,
            capability = Capability.REVOKE_STAY_PERMITS,
        )
    }

    AccessSection("Administração") {
        PermissionFlag(
            state = state,
            capability = Capability.MANAGE_ACCESS,
            danger = true,
        )
        if (state.effective.grants(Capability.MANAGE_ACCESS)) {
            P(attrs = AccessEditorDangerStyle.toAttrs()) {
                Text("Permite conceder qualquer acesso, inclusive a esta própria conta.")
            }
        }
        if (state.isSelfDemotion) {
            P(attrs = AccessEditorDangerStyle.toAttrs { attr("role", "status") }) {
                Text("Você está removendo o próprio acesso a esta página.")
            }
        }
    }

    P(attrs = AccessSummaryStyle.toAttrs()) {
        Text("Pode: " + permissionLines(state.effective).joinToString("; ") + ".")
    }
}

/** Rule + uppercase title + tight column of controls. */
@Composable
private fun AccessSection(title: String, content: @Composable () -> Unit) {
    Box(modifier = AccessSectionRuleStyle.toModifier())
    P(attrs = AccessSectionTitleStyle.toAttrs()) { Text(title.uppercase()) }
    Column(modifier = Modifier.gap(0.5.cssRem)) {
        content()
    }
}

@Composable
private fun PermissionFlag(
    state: AccessEditorState,
    capability: Capability,
    danger: Boolean = false,
) {
    val locked = state.locked(capability)
    val lastAdmin = capability == Capability.MANAGE_ACCESS && state.isLastAdmin
    val rowModifier = if (lastAdmin) {
        Modifier.gap(0.5.cssRem).attrsModifier { attr("title", LAST_ADMIN_REMOVAL_BLOCKED) }
    } else {
        Modifier.gap(0.5.cssRem)
    }
    Row(verticalAlignment = Alignment.CenterVertically, modifier = rowModifier) {
        Checkbox(
            checked = if (locked) state.sync.any { it.capability == capability } else state.flag(capability),
            onCheckedChange = { state.toggle(capability) },
            enabled = !locked && !lastAdmin,
            size = CheckboxSize.SM,
            content = {
                Span(attrs = AccessFlagTextStyle.toAttrs()) {
                    Span(attrs = if (danger) AccessDangerLabelStyle.toAttrs() else null) {
                        Text(capabilityLabel(capability))
                    }
                    Span(attrs = AccessCapabilityDescStyle.toAttrs()) {
                        Text(capabilityHelp(capability))
                    }
                }
            },
        )
        if (locked) AccessTag("sincronizado")
        if (lastAdmin) AccessTag("último admin")
    }
}

@Composable
private fun PermissionScope(
    state: AccessEditorState,
    capability: Capability,
) {
    val syncScope = state.syncScope(capability)
    val explicitScope = state.scope(capability)
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.5.cssRem)) {
        Span(attrs = AccessFlagTextStyle.toAttrs()) {
            Span { Text(capabilityLabel(capability)) }
            Span(attrs = AccessCapabilityDescStyle.toAttrs()) {
                Text(capabilityHelp(capability))
            }
        }
        Spacer()
        when {
            syncScope == GrantScope.ALL -> AccessTag("sincronizadas · todas")
            syncScope == GrantScope.OWN -> {
                // The sync owns OWN; the admin may still add the broader ALL explicitly.
                AccessTag("sincronizadas · próprias")
                Span(attrs = AccessCapabilityDescStyle.toAttrs()) { Text("adicionar:") }
                ScopeSegmented(
                    current = explicitScope,
                    options = listOf(GrantScope.GLOBAL to "Nenhuma", GrantScope.ALL to "Todas"),
                    onSelect = { state.setScope(capability, it) },
                )
            }
            else -> ScopeSegmented(
                current = explicitScope,
                options = listOf(
                    GrantScope.GLOBAL to "Nenhuma",
                    GrantScope.OWN to "Próprias",
                    GrantScope.ALL to "Todas",
                ),
                onSelect = { state.setScope(capability, it) },
            )
        }
    }
}

/** Segmented scope control — compact stand-in for a radio group. */
@Composable
private fun ScopeSegmented(
    current: GrantScope,
    options: List<Pair<GrantScope, String>>,
    onSelect: (GrantScope) -> Unit,
) {
    Row(modifier = Modifier.gap(0.25.cssRem)) {
        for ((scope, label) in options) {
            Button(
                onClick = { onSelect(scope) },
                variant = if (current == scope) PrimaryButtonVariant else DefaultButtonVariant,
                size = ButtonSize.SM,
            ) { Text(label) }
        }
    }
}

/** "dd/MM/yyyy HH:mm" -> yyyyMMddHHmm; the day-first wire format does not sort lexicographically. */
private fun stampKey(stamp: String?): Long {
    val s = stamp.orEmpty()
    if (s.length < 16) return 0L
    val day = s.substring(0, 2).toLongOrNull() ?: return 0L
    val month = s.substring(3, 5).toLongOrNull() ?: return 0L
    val year = s.substring(6, 10).toLongOrNull() ?: return 0L
    val hour = s.substring(11, 13).toLongOrNull() ?: 0L
    val minute = s.substring(14, 16).toLongOrNull() ?: 0L
    return (((year * 100L + month) * 100L + day) * 100L + hour) * 100L + minute
}

@Composable
private fun HistoryTab(state: AccessEditorState) {
    when {
        !state.historyLoaded -> P(attrs = AccessEditorHintStyle.toAttrs()) { Text("Carregando…") }
        state.historyError.isNotEmpty() ->
            P(attrs = AccessEditorErrorStyle.toAttrs()) { Text(state.historyError) }
        state.history.isEmpty() ->
            P(attrs = AccessEditorHintStyle.toAttrs()) { Text("Nenhuma alteração registrada.") }
        else -> {
            for (row in state.history.sortedByDescending { stampKey(it.grantedAt) }) {
                val grant = row.toGrantOrNull() ?: continue
                val revoked = row.revokedAt != null
                // Full details stay one hover away; the line itself stays short.
                val tooltip = buildString {
                    append(capabilityLabel(grant.capability))
                    if (grant.capability.isScoped) append(" (${scopeLabel(grant.scope).lowercase()})")
                    append(
                        if (revoked) {
                            " — revogada por ${row.revokedBy ?: "—"} em ${row.revokedAt ?: "—"}"
                        } else {
                            " — concedida por ${row.grantedBy ?: "—"} em ${row.grantedAt ?: "—"}"
                        }
                    )
                    append(" (${sourceLabel(row.source)})")
                }
                val actor = row.grantedBy
                    ?.takeIf { it.isNotBlank() && row.source != "SYNC" && row.source != "MIGRATION" }
                val meta = when {
                    revoked -> "revogada · ${shortStamp(row.revokedAt)}"
                    else -> "${actor ?: sourceLabel(row.source)} · ${shortStamp(row.grantedAt)}"
                }
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = AccessHistoryRowStyle.toModifier().gap(0.375.cssRem),
                ) {
                    Span(attrs = if (revoked) AccessHistoryRevokedStyle.toAttrs() else AccessHistoryLabelStyle.toAttrs()) {
                        Text(capabilityLabel(grant.capability))
                        if (grant.capability.isScoped) {
                            Span(attrs = AccessCapabilityDescStyle.toAttrs()) {
                                Text(" · ${scopeLabel(grant.scope).lowercase()}")
                            }
                        }
                    }
                    Spacer()
                    Span(attrs = AccessCapabilityDescStyle.toAttrs { attr("title", tooltip) }) {
                        Text(meta)
                    }
                }
            }
        }
    }
}
